import React, { useState, useEffect, useCallback, useImperativeHandle, forwardRef } from 'react';
import { Typography, LinearProgress, Sheet, Select, Option, Box } from '@mui/joy';
import createApi, { deleteExpense } from '../api';
import { monthName } from '../categoryMeta';
import SwipeableExpenseCard from './SwipeableExpenseCard';

const months = Array.from({ length: 12 }, (_, i) => i + 1);
const years = Array.from({ length: 5 }, (_, i) => new Date().getFullYear() - 2 + i);

const MonthlyExpenses = forwardRef(({ initDataRaw, onExpenseDeleted, onEditExpense }, ref) => {
    const [expenses, setExpenses] = useState([]);
    const [categories, setCategories] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [selectedMonth, setSelectedMonth] = useState(new Date().getMonth() + 1);
    const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
    const [selectedCategory, setSelectedCategory] = useState('all');
    const [deleting, setDeleting] = useState(null);

    const api = createApi(initDataRaw);

    useEffect(() => {
        const fetchCategories = async () => {
            try {
                const response = await api.get('/api/expenses/categories');
                setCategories(response.data);
            } catch (err) {
                console.error('Error fetching categories:', err);
            }
        };
        fetchCategories();
    }, [initDataRaw]);

    const fetchData = useCallback(async () => {
        try {
            setLoading(true);
            const params = {
                month: selectedMonth,
                year: selectedYear,
                category: selectedCategory !== 'all' ? selectedCategory : null
            };
            const response = await api.get('/api/expenses/all', { params });
            setExpenses(response.data.map(item => ({
                ...item,
                date: new Date(item.date)
            })));
            setLoading(false);
        } catch (err) {
            setError(err.message);
            setLoading(false);
        }
    }, [selectedMonth, selectedYear, selectedCategory, initDataRaw]);

    useImperativeHandle(ref, () => ({ refresh: fetchData }));

    useEffect(() => {
        if (initDataRaw) fetchData();
    }, [initDataRaw, fetchData]);

    const handleDelete = async (expense) => {
        setDeleting(expense.id);
        try {
            await deleteExpense(api, expense.id);
            setExpenses(expenses.filter(e => e.id !== expense.id));
            onExpenseDeleted?.();
            window.Telegram?.WebApp?.HapticFeedback?.notificationOccurred('success');
        } catch (err) {
            console.error('Delete error:', err);
            window.Telegram?.WebApp?.HapticFeedback?.notificationOccurred('error');
        } finally {
            setDeleting(null);
        }
    };

    if (loading) return <LinearProgress />;
    if (error) return <Typography color="danger">Error: {error}</Typography>;

    return (
        <Box sx={{ p: 1 }}>
            <Sheet sx={{ display: 'flex', gap: 1, mb: 2, bgcolor: 'transparent' }}>
                <Select value={selectedMonth} onChange={(e, v) => setSelectedMonth(v)} size="sm" sx={{ minWidth: 120 }}>
                    {months.map(month => (
                        <Option key={month} value={month}>{monthName(month)}</Option>
                    ))}
                </Select>
                <Select value={selectedYear} onChange={(e, v) => setSelectedYear(v)} size="sm" sx={{ minWidth: 90 }}>
                    {years.map(year => (
                        <Option key={year} value={year}>{year}</Option>
                    ))}
                </Select>
                <Select value={selectedCategory} onChange={(e, v) => setSelectedCategory(v)} size="sm" sx={{ minWidth: 90 }}>
                    <Option value="all">Все</Option>
                    {categories.map(category => (
                        <Option key={category} value={category}>{category.toLowerCase()}</Option>
                    ))}
                </Select>
            </Sheet>

            {expenses.length > 0 ? (
                expenses.map(expense => (
                    <SwipeableExpenseCard
                        key={expense.id}
                        expense={expense}
                        dateFormat="dd.MM"
                        onEdit={onEditExpense}
                        onDelete={handleDelete}
                        deleting={deleting === expense.id}
                    />
                ))
            ) : (
                <Typography level="body-sm" color="neutral" sx={{ py: 3, textAlign: 'center' }}>
                    Нет данных за выбранный период
                </Typography>
            )}
        </Box>
    );
});

export default MonthlyExpenses;
